import { JsonFormatter } from 'tslint/lib/formatters';
import { Component, Input, OnChanges, ElementRef } from '@angular/core';

import { DataConsumerConfiguration, NameValue } from '../../../../../interfaces/WidgetInterfaces';
import { BaseWidget } from '../../../BaseWidget/BaseWidget.component';
import { DataPolicy } from '../../../../../interfaces/WidgetInterfaces';
import { DataSetFilter } from '../../../../../classes/DataSetFilter';
import { DisplayStateService } from '../../../../../services/DisplayStateService';
import { AppStateService } from '../../../../../services/AppStateService';
import { WebPlatformApiService } from '../../../../../services/WebPlatformApiService';
import { ClientSettingsService } from '../../../../../services/ClientSettingsService';

@Component({
  selector: 'app-data-consumer-config-editor',
  template: `<ng-template #dataConsumerConfigName>
    <b>Data consumer config name (optional):</b> Give a unique name to your data consumer config to identify it quickly</ng-template>
<div class="form-group row" style="margin-top: 15px">
    <label [tooltip]="dataConsumerConfigName" class="col-sm-2 col-form-label">Data Consumer Config Name (optional)</label>
    <div class="col-sm-10">
        <input type="text" class="form-control" [(ngModel)]="DataConsumerConfig.name" name="dataConsumerConfigName">
    </div>
</div>

<ng-template #dataPolicyName>
    <b>Data policy name:</b> must be a name in the list of data configuration objects </ng-template>
<div class="form-group row" style="margin-top: 15px">
    <label [tooltip]="dataPolicyName" class="col-sm-2 col-form-label">Data Policy Name</label>
    <div class="col-sm-10">

        <select class="form-control" [(ngModel)]="DataConsumerConfig.dataPolicy" (change)="onSelectedDataPolicyChange($event.target.value)"
            id="dataPolicy">
            <option *ngFor="let name of dataPolicyNames" [ngValue]="name">
                {{name}}
            </option>
        </select>
    </div>
</div>

<tabset type="pills">

    <ng-template #fixedParamsTooltip>
        <b>Fixed Parameters:</b> These parameter name/values are ALWAYS sent with the data request
    </ng-template>
    <tab heading="Fixed" *ngIf="showMiddleLayerFeatures">
        <span class="label label-default" [tooltip]="fixedParamsTooltip" placement="bottom">
            <i class="fa fa-info-circle"></i>&nbsp;Hover for info</span>
        <app-name-value-editor [nvArray]="DataConsumerConfig.fixedParameters" [nameDisplay]="'Name'" [valueDisplay]="'Value'"></app-name-value-editor>
    </tab>

    <ng-template #sortColumnsTooltip>
        <b>Fixed Parameters:</b> These parameter name/values used to sort. The name is the column, and the value is asc or desc
    </ng-template>
    <tab heading="Sorting" *ngIf="showMiddleLayerFeatures">
        <span class="label label-default" [tooltip]="sortColumnsTooltip" placement="bottom">
            <i class="fa fa-info-circle"></i>&nbsp;Hover for info</span>
        <app-name-value-editor [nvArray]="DataConsumerConfig.sortColumns" [valueEnum]="sortTypes" [nameEnum]="DataConsumerConfig.columns"
            [nameDisplay]="'column'" [valueDisplay]="'Value'"></app-name-value-editor>
    </tab>

    <ng-template #contextParamsTooltip>
        <div style="text-align: left;">
            <b>Context Parameters:</b> Parameters sent to the data source based on current context. Specifying a 'Context Name'
            tells the app that the context name/value should be sent to the data source url. Specifying 'Name To Send' will
            send that value as the name instead of the context name. Example
            <ul>
                <li>Current context name/values are [Source=probe][Design=L06A][Lot=1234567.001][Step=FPP.00]</li>
                <li>context parameters are [Design=,Step=Pid]</li>
                <li>query params would be ?Design=L06A&Pid=FPP.00</li>
            </ul>
        </div>
    </ng-template>
    <tab heading="Context Params" *ngIf="showMiddleLayerFeatures">
        <span class="label label-default" [tooltip]="contextParamsTooltip" placement="bottom">
            <i class="fa fa-info-circle"></i>&nbsp;Hover for info</span>
        <app-name-value-editor [nvArray]="DataConsumerConfig.contextParameters" [nameDisplay]="'Context Name'" [valueDisplay]="'Name To Send (optional)'"></app-name-value-editor>
    </tab>

    <ng-template #filterConfiguration>
        <b>Filter Configuration: </b>
        <ul>
            <li>
                <b>Name: </b>Column name to filter on</li>
            <li>
                <b>operator</b> (optional) specifies the kind of filtering
                <ul>
                    <li>string filtering: eq (default), startsWith, endsWith, contains</li>
                    <li>number filtering: ==, gt, lt, ge, le, ne</li>
                </ul>
            </li>
            <li>
                <b>Fixed value</b> (optional) the value to use with the operator to filter the rows</li>
            <li>
                <b>Context Name for Value</b> (optional) the current context name to use the value for to filter the rows</li>
            <li>
                <b>Server side filtering</b> If true, server will filter results before sending to the app. If false, filtering
                occurs in app after it is retrieved</li>
        </ul>
    </ng-template>
    <tab heading="Filter" *ngIf="showMiddleLayerFeatures">
        <span class="label label-default" [tooltip]="filterConfiguration" placement="bottom">
            <i class="fa fa-info-circle"></i>&nbsp;Hover for info</span>
        <div class="form-group row">
            <label class="col-sm-3 col-form-label">Column Name</label>
            <label class="col-sm-2 col-form-label">Operator?</label>
            <label class="col-sm-2 col-form-label">Fixed Value?</label>
            <label class="col-sm-2 col-form-label">Context Name for Value?</label>
            <label class="col-sm-2 col-form-label">Server side filtering</label>

        </div>
        <div class="form-group row" *ngFor="let fRule of DataConsumerConfig.filterConfiguration">

            <div *ngIf="DataConsumerConfig.columns; else filterNameNoColumns" class="col-sm-3">
                <select class="form-control" [(ngModel)]="fRule.column" id="fRuleColumn">
                    <option *ngFor="let name of DataConsumerConfig.columns" [ngValue]="name">
                        {{name}}
                    </option>
                </select>
            </div>
            <ng-template #filterNameNoColumns>
                <div class="col-sm-3">
                    <input type="text" class="form-control" [(ngModel)]="fRule.column" name="column">
                </div>
            </ng-template>
            <div class="col-sm-2">
                <select class="form-control" [(ngModel)]="fRule.operator" id="fRuleOperator">
                    <option *ngFor="let nv of filterOps" [ngValue]="nv.value">
                        {{nv.value}}
                    </option>
                </select>
            </div>
            <div class="col-sm-2">
                <input type="text" class="form-control" [(ngModel)]="fRule.value" name="value">
            </div>
            <div class="col-sm-2">
                <input type="text" class="form-control" [(ngModel)]="fRule.contextValueName" name="contextValueName">
            </div>

            <div class="col-sm-2">
                <select class="form-control" [(ngModel)]="fRule.serverSide" name="serverSide">
                    <option [ngValue]="true">True</option>
                    <option [ngValue]="false">False</option>
                </select>
            </div>
            <div class="col-sm-1">
                <button type="button" class="btn btn-sm btn-danger" (click)="DeleteFilterConfigByIndex(idx)">
                    <i class="fa fa-times"></i>
                </button>
            </div>
        </div>
        <button type="button" class="btn btn-sm btn-outline-primary" (click)="AddFilterConfig()">Add</button>
    </tab>

    <ng-template #overrideCacheRulesTooltip>
        Used to override the data policy set cache rules for this widget
    </ng-template>
    <tab heading="Cache rule overrides" *ngIf="showMiddleLayerFeatures">
        <span class="label label-default" [tooltip]="overrideCacheRulesTooltip" placement="bottom">
            <i class="fa fa-info-circle"></i>&nbsp;Hover for info</span>
        <app-cache-rule-editor [cacheRules]="DataConsumerConfig.overrideCacheRules"></app-cache-rule-editor>
    </tab>

    <ng-template #dataGroupingTooltip>
        Used to override the data policy set cache rules for this widget
    </ng-template>
    <tab heading="Grouping" *ngIf="showMiddleLayerFeatures">
        <span class="label label-default" [tooltip]="dataGroupingTooltip" placement="bottom">
            <i class="fa fa-info-circle"></i>&nbsp;Hover for info</span>
        <app-data-grouping-editor [columns]="DataConsumerConfig.columns" [dataGrouping]="DataConsumerConfig.dataGrouping"></app-data-grouping-editor>
    </tab>


    <ng-template #pivotTooltip>
        Not implemented yet
    </ng-template>
    <tab heading="Pivot" *ngIf="showMiddleLayerFeatures">
        <span class="label label-default" [tooltip]="pivotTooltip" placement="bottom">
            <i class="fa fa-info-circle"></i>&nbsp;Hover for info</span>
        <p>Not Implemented yet</p>
    </tab>

    <tab heading="Fixed Data" *ngIf="showMiddleLayerFeatures">
        <button type="button" class="btn btn-info btn-sm" (click)="UpdateFixedData()">
            Save Config Changes
        </button>
        <textarea style="width: 100%; height: 400px" [(ngModel)]="FixedData" name="fixedData"></textarea>
    </tab>

    <tab heading="Columns" *ngIf="showMiddleLayerFeatures">
        <div *ngIf="DataConsumerConfig.columns">
            <label>These are columns saved the last time the platform executed this data consumer configuration or from those that
                existing when this widget was cloned. They may be out of date - if so, use the 'Clear Columns' button.
            </label>
            <button type="button" (click)="ClearColumns()" class="btn btn-info btn-sm">Clear Columns</button>
            <ul>
                <li *ngFor="let name of DataConsumerConfig.columns">{{name}}</li>
            </ul>
        </div>
    </tab>

    <tab heading="Custom Headers">
        <app-name-value-editor [nvArray]="DataConsumerConfig.overrideCustomHeaders" [nameDisplay]="'Header Name'" [valueDisplay]="'Header Value'"></app-name-value-editor>
    </tab>

    <tab heading="JSON" *ngIf="showMiddleLayerFeatures">
        <button type="button" class="btn btn-info btn-sm" (click)="LoadJson()">
            Serialize to JSON...
        </button>
        <button type="button" class="btn btn-info btn-sm" (click)="UpdateFromJson()">
            Save JSON Changes
        </button>
        <textarea style="width: 100%; height: 400px" [(ngModel)]="JsonData" name="JsonData"></textarea>
    </tab>
</tabset>`,
  styles: [``]
})
export class DataConsumerConfigEditorComponent extends BaseWidget implements OnChanges {

  @Input() DataConsumerConfig: DataConsumerConfiguration;

  dataPolicyNames: string[] = [];
  dataPolicyList: DataPolicy[];
  showMiddleLayerFeatures: boolean = true;

  sortTypes = ['asc', 'desc'];

  filterOps = DataSetFilter.FilterOperationMap();
  JsonData: string;
  FixedData: string;

  constructor(
    protected element: ElementRef,
    protected clientSettingsService: ClientSettingsService,
    protected displayStateService: DisplayStateService,
    protected appStateService: AppStateService,
    protected webPlatformApiService: WebPlatformApiService) {
      super(element, clientSettingsService, displayStateService, appStateService, webPlatformApiService);
    }
  ngOnChanges() {
    this.FixedData = this.DataConsumerConfig && this.DataConsumerConfig.fixedData ? JSON.stringify(this.DataConsumerConfig.fixedData) : '';
    // Because DCC name is optional we removed it when it's manually cleared
    if (this.DataConsumerConfig && this.DataConsumerConfig.name === '') {
      this.DataConsumerConfig.name = undefined;
    }
    this.initializeHeaders();
  }

  onInitialize() {
    const empty = [''];

    this.webPlatformApiService.GetConfig<DataPolicy[]>('DataPolicyList').subscribe(x => {
      this.dataPolicyNames = empty.concat(x.map(dp => { return dp.name; }));
      this.dataPolicyList = x;
      this.onSelectedDataPolicyChange(this.DataConsumerConfig.dataPolicy);
    });
    this.FixedData = this.DataConsumerConfig && this.DataConsumerConfig.fixedData ? JSON.stringify(this.DataConsumerConfig.fixedData) : '';
    this.initializeHeaders();
  }

  ClearColumns() {
    this.DataConsumerConfig.columns = undefined;
  }

  initializeHeaders() {
    if (!this.DataConsumerConfig.overrideCustomHeaders) {
      this.DataConsumerConfig.overrideCustomHeaders = [];
    }
  }
  UpdateFixedData() {

    if (this.DataConsumerConfig) {
      try {
        if (this.FixedData && this.FixedData.length > 0) {
          this.DataConsumerConfig.fixedData = JSON.parse(this.FixedData);
        } else {
          this.DataConsumerConfig.fixedData = undefined;
        }

      } catch (e) {
        alert(JSON.stringify(e));
      }
    }

  }

  DeleteFilterConfigByIndex(idx) {
    this.DataConsumerConfig.filterConfiguration.splice(idx, 1);
  }

  AddFilterConfig() {
    this.DataConsumerConfig.filterConfiguration.push({ column: '', contextValueName: '', operator: '', serverSide: false, value: '' });
  }

  LoadJson() {
    this.JsonData = JSON.stringify(this.DataConsumerConfig, null, 2);
  }

  UpdateFromJson() {

    if (this.DataConsumerConfig) {
      try {
        const tempDCC = JSON.parse(this.JsonData);

        this.DataConsumerConfig.name = tempDCC.name;
        this.DataConsumerConfig.columns = tempDCC.columns;
        this.DataConsumerConfig.contextParameters = tempDCC.contextParameters;
        this.DataConsumerConfig.dataGrouping = tempDCC.dataGrouping;
        this.DataConsumerConfig.dataPolicy = tempDCC.dataPolicy;
        this.DataConsumerConfig.filterConfiguration = tempDCC.filterConfiguration;
        this.DataConsumerConfig.fixedData = tempDCC.fixedData;
        this.DataConsumerConfig.fixedParameters = tempDCC.fixedParameters;
        this.DataConsumerConfig.overrideCacheRules = tempDCC.overrideCacheRules;
        this.DataConsumerConfig.pivot = tempDCC.pivot;
        this.DataConsumerConfig.sortColumns = tempDCC.sortColumns;
      } catch (e) {
        alert(JSON.stringify(e));
      }
    }
  }

  onSelectedDataPolicyChange(dataPolicyName: string) {
    if (this.dataPolicyList.length) {
      const dataPolicy = this.dataPolicyList.find(dp => dp.name === this.DataConsumerConfig.dataPolicy);
      if (dataPolicy) {
        if (dataPolicy.useMiddleLayer || typeof dataPolicy.useMiddleLayer === 'undefined') {
          this.showMiddleLayerFeatures = true;
          this.DataConsumerConfig.directUri = null;
          this.DataConsumerConfig.apiStoreAppKey = null;
          this.DataConsumerConfig.apiStoreSendMtAdfsOpenId = null;
          this.DataConsumerConfig.httpMethod = null;
        } else {
          // for any dataPolicy we'se setup to not use the middle layer, we need to store the Uri in the DataConsumerConfig
          this.DataConsumerConfig.directUri = dataPolicy.sourceURL;
          // and the apiStoreAppKey
          this.DataConsumerConfig.apiStoreAppKey = dataPolicy.apiStoreAppKey;
          // and the httpMethod
          this.DataConsumerConfig.httpMethod = dataPolicy.httpMethod;
          // and the apiStoreSendMtAdfsOpenId
          this.DataConsumerConfig.apiStoreSendMtAdfsOpenId = dataPolicy.apiStoreSendMtAdfsOpenId;
          // for any dataPolicy we've setup to not use the middle layer.. disable a bunch of the other config that does not apply
          this.showMiddleLayerFeatures = false;
        }
      }

    }
  }
}
